import React, { useCallback, useRef, useState } from 'react';
import { SelectionRange, selectRange } from '@/shared/ui/data/EditableGrid/lib/selection';
import { Position } from '@/shared/ui/data/EditableGrid/types/core.type';

interface IOptions {
  onSelectionChange?: (positions: ReadonlyArray<Position>) => void;
}

export function useDragSelection(options: IOptions = {}) {
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStart, setDragStart] = useState<Position | null>(null);
  const [dragEnd, setDragEnd] = useState<Position | null>(null);
  const [currentSelection, setCurrentSelection] = useState<ReadonlyArray<Position>>([]);

  const isDraggingRef = useRef(false);

  const handleMouseDown = useCallback(
    (position: Position, e: React.MouseEvent) => {
      const LEFT_CLICK = 0;
      if (e.button !== LEFT_CLICK || e.shiftKey || e.ctrlKey || e.metaKey) return;

      e.preventDefault();
      setIsDragging(true);
      isDraggingRef.current = true;
      setDragStart(position);
      setDragEnd(position);

      const selection = selectRange({ start: position, end: position });
      setCurrentSelection(selection);
      options.onSelectionChange?.(selection);
    },
    [options]
  );

  const handleMouseEnter = useCallback(
    (position: Position) => {
      if (!isDraggingRef.current || !dragStart) return;

      setDragEnd(position);

      const range: SelectionRange = { start: dragStart, end: position };
      const selection = selectRange(range);
      setCurrentSelection(selection);
      options.onSelectionChange?.(selection);
    },
    [dragStart, options]
  );

  const handleMouseUp = useCallback(() => {
    if (!isDraggingRef.current) return;

    setIsDragging(false);
    isDraggingRef.current = false;
  }, []);

  const cancelDrag = useCallback(() => {
    setIsDragging(false);
    isDraggingRef.current = false;
    setDragStart(null);
    setDragEnd(null);
  }, []);

  return {
    isDragging,
    dragStart,
    dragEnd,
    currentSelection,
    handleMouseDown,
    handleMouseEnter,
    handleMouseUp,
    cancelDrag,
  };
}
